import React, { useEffect } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useDispatch, useSelector } from "react-redux";
import { apiCall } from "../../api";
import { login } from "../../slices/TokensSlice";
import { saveUser } from "../../slices/UserSlice";
import "./LoginPage.css";

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const BoltIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const SparkleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
  </svg>
);

const features = [
  { icon: <BoltIcon />, text: "Enroll AI agent: automate student admission queries and applications" },
  { icon: <ShieldIcon />, text: "Retain AI agent: automate student support inside the LMS" },
  { icon: <SparkleIcon />, text: "Risk AI: detect drop-off risk and nudge students to re-engage" },
];

const AGENT_SERVICE_ORIGIN = (() => {
  try { return new URL(process.env.REACT_APP_AGENT_SERVICE_URL).origin; } catch { return null; }
})();

function redirectWithToken(next, userToken) {
  try {
    const nextUrl = new URL(next);
    if (!AGENT_SERVICE_ORIGIN || nextUrl.origin !== AGENT_SERVICE_ORIGIN) {
      console.warn("LoginPage: blocked redirect to untrusted origin:", nextUrl.origin);
      return;
    }
    nextUrl.searchParams.set("token", userToken);
    window.location.href = nextUrl.toString();
  } catch {
    console.warn("LoginPage: invalid next URL, ignoring");
  }
}

function LoginPage() {
  const dispatch = useDispatch();
  const tokens = useSelector((state) => state?.tokens?.value);
  const user = useSelector((state) => state?.user?.value);

  const _next = new URLSearchParams(window.location.search).get("next");
  const _redirecting =
    tokens?.user_token &&
    (_next || (user && window.location.pathname === "/login"));

  useEffect(() => {
    if (!tokens?.user_token) return;
    const params = new URLSearchParams(window.location.search);
    const next = params.get("next");
    if (next) {
      redirectWithToken(next, tokens.user_token);
    } else if (user && window.location.pathname === "/login") {
      window.location.href = "/";
    }
  }, [tokens, user]);

  const refreshTokens = (tokens) => dispatch(login(tokens));

  const getUser = async (tokens) => {
    try {
      const user = await apiCall(
        "/v1/users/get_info",
        { method: "GET", authTokens: tokens },
        refreshTokens
      );
      if (user) dispatch(saveUser(user));
    } catch (error) {
      console.error("Error while getting user info:", error);
    }
  };

  const handleSuccess = async (credentialResponse) => {
    try {
      const tokens = await apiCall("/auth/google", {
        method: "POST",
        body: { code: credentialResponse.code },
      });
      dispatch(login(tokens));
      await getUser(tokens);

      const params = new URLSearchParams(window.location.search);
      const next = params.get("next");
      if (next && tokens?.user_token) {
        redirectWithToken(next, tokens.user_token);
      } else {
        window.location.href = "/";
      }
    } catch (error) {
      console.error("Error exchanging authorization code:", error);
    }
  };

  const loginWithGoogle = useGoogleLogin({
    onSuccess: handleSuccess,
    onError: (err) => console.error("Google login failed", err),
    flow: "auth-code",
  });

  if (_redirecting) return null;

  return (
    <div className="lp-root">
      {/* ── Left panel ─────────────────────────────────────────────────────── */}
      <div className="lp-left">
        <div className="lp-left-inner">
          {/* Logo */}
          <div className="lp-logo">
            <img src="/insync_favicon.png" alt="InSyncAI" className="lp-logo-img" />
            <span className="lp-logo-text">InSyncAI Studio</span>
          </div>

          {/* Headline */}
          <h1 className="lp-headline">Build Powerful AI Agents in Minutes</h1>
          <p className="lp-desc">
            No-code platform to create, configure, and deploy intelligent AI
            agents across multiple channels.
          </p>

          {/* Features */}
          <ul className="lp-features">
            {features.map((f, i) => (
              <li key={i} className="lp-feature-item">
                <span className="lp-feature-icon">{f.icon}</span>
                <span className="lp-feature-text">{f.text}</span>
              </li>
            ))}
          </ul>

          {/* Stats */}
          <div className="lp-stats">
            <div className="lp-stat">
              <span className="lp-stat-value">96%</span>
              <span className="lp-stat-label">Median deflection rate</span>
            </div>
            <div className="lp-stat">
              <span className="lp-stat-value">92%</span>
              <span className="lp-stat-label">Median CSAT for AI</span>
            </div>
            <div className="lp-stat">
              <span className="lp-stat-value">20K</span>
              <span className="lp-stat-label">Hours saved yearly</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right panel ────────────────────────────────────────────────────── */}
      <div className="lp-right">
        <div className="lp-form">
          <h2 className="lp-welcome">Welcome back</h2>
          <p className="lp-welcome-sub">Sign in to continue building amazing AI agents</p>

          <button className="lp-google-btn" onClick={() => loginWithGoogle()}>
            <GoogleIcon />
            <span>Continue with Google</span>
          </button>

          <p className="lp-support">
            Need help signing in?{" "}
            <a href="mailto:support@insyncai.com" className="lp-support-link">
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
